import { useContext, useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../../../utils/axiosInstance";
import BackButton from "../../../components/Button/BackButton";
import { toast } from "../../../components/Toast/toast";
import { AuthContext } from "../../../context/AuthContext";
import { MODULES, isReadOnly } from "../../../utils/constants/permissions";
import { btn } from "../../../components/Button/buttonStyles";
import InvoiceTaxRows, {
  INVOICE_TAX_HEADERS,
  invoiceTaxTotals,
} from "../../../components/Invoice/InvoiceTaxRows";
import { adjustmentLabel } from "../../../utils/adjustments";

const fmtNum = (v) =>
  v !== undefined && v !== null && v !== "" ? Number(v).toFixed(2) : "-";

const fmtDateTime = (d) => {
  if (!d) return "-";
  return new Date(d).toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

const daysFromNow = (d) => {
  if (!d) return null;
  return Math.ceil((new Date(d) - new Date()) / (1000 * 60 * 60 * 24));
};

const Row = ({ label, children, value }) => (
  <div className="flex items-start">
    <span className="w-44 text-sm font-medium text-gray-700 shrink-0">{label}:</span>
    <span className="text-sm text-gray-900">{children ?? value ?? "-"}</span>
  </div>
);

const Section = ({ title, count, actionBtn, children }) => (
  <div className="bg-white shadow rounded-lg overflow-hidden">
    <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
      <span className="text-xl font-bold text-gray-900">{title}</span>
      {actionBtn}
    </div>
    {count !== undefined && (
      <p className="text-xs text-gray-500 px-5 pt-3">
        Total <span className="font-semibold">{count}</span>{" "}
        {count === 1 ? "item" : "items"}.
      </p>
    )}
    {children}
  </div>
);

const SectionTable = ({ headers, children }) => (
  <div className="overflow-x-auto px-5 pb-4 pt-2">
    <table className="w-full text-xs border-collapse">
      <thead>
        <tr className="border-b border-gray-200">
          {headers.map((h) => (
            <th
              key={h}
              className="text-left font-semibold text-gray-700 py-2 pr-4 whitespace-nowrap"
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

const ActionBtn = ({ onClick, label, color }) => (
  <button
    onClick={onClick}
    className={`text-xs text-white px-3 py-1.5 rounded shadow ${color}`}
  >
    {label}
  </button>
);

const AdminVendorInvoiceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  // Project managers get a view-only vendor invoice: no edit, delete, write-off
  // or messaging controls (the API rejects those writes anyway).
  const readOnly = isReadOnly(user, MODULES.VENDOR_INVOICES);
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState(
    sessionStorage.getItem("invoiceSuccessMsg") || ""
  );

  const fetchInvoice = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/project-finances/vendor-invoices/${id}`, {
        withCredentials: true,
      });
      setInvoice(res.data.data);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch invoice details.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    sessionStorage.removeItem("invoiceSuccessMsg");
    if (id) fetchInvoice();
  }, [id, fetchInvoice]);

  if (loading)
    return (
      <div className="flex justify-center items-center mt-16">
        <div className="animate-spin rounded-full h-8 w-8 border border-gray-300 border-t-brand-600" />
      </div>
    );
  if (error)
    return <div className="text-center mt-10 text-danger-600 font-medium">{error}</div>;
  if (!invoice)
    return <div className="text-center mt-10 text-gray-500">Invoice not found.</div>;

  const recv = invoice.flatRateReceivable || invoice.unitBasedReceivable;
  const isUnit = invoice.receivableType === "unit_based";
  const currCode = invoice.currency?.currency?.code || "-";

  const vendorName = (() => {
    const v = invoice.vendor;
    if (!v) return "-";
    if (v.company_name) return v.company_name;
    const u = v.primary_users;
    return u ? `${u.first_name} ${u.last_name}`.trim() : "-";
  })();

  const contactPerson = (() => {
    const cp = invoice.job?.vendorContactPerson;
    if (!cp) return "-";
    return `${cp.first_name} ${cp.last_name || ""}`.trim();
  })();

  const pmName =
    invoice.paymentMethod?.payment_method?.replace(/_/g, " ") || "-";

  const due = Number(invoice.due_amount || 0);
  const paid = Number(invoice.paid_amount || 0);
  const totals = invoiceTaxTotals(invoice);
  const total = totals.total;
  const writtenOff = Number(invoice.write_off_amount || 0);
  // Nothing is left to collect, so payment coverage no longer applies.
  const fullyWrittenOff = writtenOff > 0 && writtenOff >= total;
  const coverageLabel =
    total === 0
      ? "Not covered"
      : due <= 0
      ? "Fully covered"
      : paid > 0
      ? "Partially covered"
      : "Not covered";
  const coverageColor =
    coverageLabel === "Fully covered"
      ? "text-green-600"
      : coverageLabel === "Partially covered"
      ? "text-amber-600"
      : "text-danger-500";

  const days = daysFromNow(invoice.due_at);

  return (
    <div className="space-y-6">
      {successMsg && (
        <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 flex justify-between items-center text-sm text-green-800">
          <span>{successMsg}</span>
          <button
            onClick={() => setSuccessMsg("")}
            className="text-green-600 hover:text-green-800 font-bold"
          >
            ✕
          </button>
        </div>
      )}

      {/* Top two-card row */}
      <div className="flex gap-6 items-start">
        {/* Left card */}
        <div className="w-[63%] bg-white shadow rounded-lg p-8">
          <div className="flex justify-between items-center mb-8">
            <div className="flex items-center gap-3">
              <BackButton />
              <h2 className="text-xl font-bold text-gray-900">
                Invoice {invoice.invoice_code}
              </h2>
            </div>
            <div className="space-x-3">
              <button
                onClick={() => toast.error("Download as PDF not yet implemented")}
                className={btn("secondary", "sm")}
              >
                Download as PDF
              </button>
              {!readOnly && (
                <>
                  <button
                    onClick={() => navigate(`/vendor/invoice/${id}/adjustments`)}
                    className={btn("secondary", "sm")}
                  >
                    Price adjustments
                  </button>
                  <button
                    onClick={() => toast.error("Update not yet implemented")}
                    className={btn("primary", "sm")}
                  >
                    Update
                  </button>
                  <button
                    onClick={() => toast.error("Delete not yet implemented")}
                    className={btn("danger", "sm")}
                  >
                    Delete
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="space-y-4 pl-4">
            <Row label="Vendor">
              {invoice.vendor_id ? (
                <Link
                  to={`/vendors/${invoice.vendor_id}`}
                  className="text-brand-600 hover:text-brand-800 underline text-sm"
                >
                  {vendorName}
                </Link>
              ) : (
                vendorName
              )}
            </Row>
            <Row label="Vendor contact person" value={contactPerson} />
            <Row label="Issued at" value={fmtDateTime(invoice.issued_at)} />
            <Row label="Payment terms" value="45 days" />
            <Row label="Payment method" value={pmName} />
            <Row label="Currency" value={currCode} />
            <Row label="Subtotal" value={fmtNum(totals.subtotal)} />
            <Row label="Extra charge" value={invoice.extra_charge_type ? fmtNum(totals.extraCharge) : "-"} />
            <Row label="Discount" value={invoice.discount_type ? fmtNum(totals.discount) : "-"} />
            <Row label="Deduction" value={invoice.deduction_type ? fmtNum(totals.deduction) : "-"} />
            <Row label="Total (before taxes)" value={fmtNum(totals.beforeTaxes)} />
            <Row label="Taxes" value={fmtNum(totals.taxTotal)} />
            <Row label="Total" value={fmtNum(totals.total)} />
            <Row label="Legal entity" value={invoice.vendor?.legal_entity || "-"} />
            <Row label="Filename" value={recv?.file?.file_name || recv?.file?.file_code || "-"} />
            <Row label="Note for vendor" value={recv?.note_for_vendor || "-"} />
            <Row label="PDF template" value="-" />
            <Row label="Created at" value={fmtDateTime(invoice.createdAt)} />
          </div>
        </div>

        {/* Right card (Status) */}
        <div className="flex-1 bg-white shadow rounded-lg p-8">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-xl font-bold text-gray-900">Status</h2>
            {!readOnly && (
              <div className="flex gap-2">
                <button
                  onClick={() => navigate(`/vendor/invoice/${id}/write-off`)}
                  className={btn("secondary", "sm")}
                >
                  Write off
                </button>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="flex items-start">
              <span className="w-44 text-sm font-medium text-gray-700 shrink-0">
                Due at:
              </span>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-gray-900">
                  {fmtDateTime(invoice.due_at)}
                </span>
                {days !== null && (
                  <span
                    className={`text-xs font-medium ${
                      days < 0 ? "text-danger-500" : "text-amber-500"
                    }`}
                  >
                    {days < 0
                      ? `${Math.abs(days)} days overdue`
                      : `in ${days} days`}
                  </span>
                )}
              </div>
            </div>

            <div className="pt-2">
              <p className="text-sm font-bold text-gray-900 mb-4">
                Payment coverage
              </p>
              <div className="space-y-4">
                {!fullyWrittenOff && (
                  <div className="flex items-start">
                    <span className="w-44 text-sm font-medium text-gray-700 shrink-0">
                      Payment coverage:
                    </span>
                    <span className={`text-sm font-medium ${coverageColor}`}>
                      {coverageLabel}
                    </span>
                  </div>
                )}
                {!fullyWrittenOff && (
                  <div className="flex items-start">
                    <span className="w-44 text-sm font-medium text-gray-700 shrink-0">
                      Paid amount:
                    </span>
                    <span className="text-sm text-gray-900">
                      {fmtNum(invoice.paid_amount)}
                    </span>
                  </div>
                )}
                {Number(invoice.write_off_amount) > 0 && (
                  <>
                    <div className="flex items-start">
                      <span className="w-44 text-sm font-medium text-gray-700 shrink-0">
                        Write off amount:
                      </span>
                      <span className="text-sm text-gray-900">{fmtNum(invoice.write_off_amount)}</span>
                    </div>
                    <div className="flex items-start">
                      <span className="w-44 text-sm font-medium text-gray-700 shrink-0">
                        Written off at:
                      </span>
                      <span className="text-sm text-gray-900">{fmtDateTime(invoice.written_off_at)}</span>
                    </div>
                  </>
                )}
                <div className="flex items-start">
                  <span className="w-44 text-sm font-medium text-gray-700 shrink-0">
                    Due amount:
                  </span>
                  <span className="text-sm font-bold text-gray-900">
                    {fmtNum(invoice.due_amount)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Taxes */}
      <Section title="Taxes" count={invoice.taxes?.length ?? 0}>
        <SectionTable headers={INVOICE_TAX_HEADERS}>
          <InvoiceTaxRows taxes={invoice.taxes} />
        </SectionTable>
      </Section>

      {/* Payables */}
      <Section
        title="Payables"
        count={recv ? 1 : 0}
        actionBtn={
          <ActionBtn
            onClick={() => toast.error("Add payables not yet implemented")}
            label="Add payables"
            color="bg-brand-600 hover:bg-brand-700"
          />
        }
      >
        <div className="overflow-x-auto px-5 pb-4 pt-2">
          <table className="w-full text-xs border-collapse min-w-[1400px]">
            <thead>
              <tr className="border-b border-gray-200">
                {[
                  "Code", "Job", "Job service", "Job language pair",
                  "Unit amount", "Unit", "Price per unit", "Subtotal",
                  "Extra charge", "Discount", "Total",
                  "Filename", "Note for vendor", "Internal note", "Issued at", "",
                ].map((h) => (
                  <th
                    key={h}
                    className="text-left font-semibold text-gray-700 py-2 pr-4 whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recv ? (
                <tr className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-2 pr-4 whitespace-nowrap">
                    <span className="text-brand-600 font-medium">{recv.payable_code || "—"}</span>
                  </td>
                  <td className="py-2 pr-4 whitespace-nowrap">
                    <Link
                      to={`/project/${invoice.project_id}/job/${invoice.job_id}`}
                      className="text-brand-600 hover:underline font-medium"
                    >
                      {invoice.job?.name || "—"}
                    </Link>
                  </td>
                  <td className="py-2 pr-4 text-gray-700 whitespace-nowrap">
                    {invoice.job?.service?.name || "—"}
                  </td>
                  <td className="py-2 pr-4 text-gray-700 whitespace-nowrap">
                    {invoice.job?.languagePair
                      ? `${invoice.job.languagePair.sourceLanguage?.name || ""} → ${invoice.job.languagePair.targetLanguage?.name || ""}`
                      : "—"}
                  </td>
                  <td className="py-2 pr-4 text-right text-gray-700">
                    {isUnit ? fmtNum(recv.unit_amount) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="py-2 pr-4 text-gray-700">
                    {isUnit ? (recv.unit?.name || "—") : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="py-2 pr-4 text-right text-gray-700">
                    {isUnit ? fmtNum(recv.price_per_unit) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="py-2 pr-4 font-semibold text-gray-900 text-right whitespace-nowrap">
                    {fmtNum(recv.subtotal)}
                  </td>
                  <td className="py-2 pr-4 text-right text-gray-700">{adjustmentLabel(recv, "extra_charge")}</td>
                  <td className="py-2 pr-4 text-right text-gray-700">{adjustmentLabel(recv, "discount")}</td>
                  <td className="py-2 pr-4 font-semibold text-gray-900 text-right whitespace-nowrap">
                    {fmtNum(recv.total ?? recv.subtotal)}
                  </td>
                  {/* <td className="py-2 pr-4 text-gray-400">-</td> */}
                  <td className="py-2 pr-4 text-gray-700 whitespace-nowrap">
                    {recv.file?.file_name || recv.file?.file_code || "—"}
                  </td>
                  <td className="py-2 pr-4 text-gray-700">
                    {recv.note_for_vendor || "—"}
                  </td>
                  <td className="py-2 pr-4 text-gray-700">
                    {recv.internal_note || "—"}
                  </td>
                  <td className="py-2 pr-4 whitespace-nowrap text-gray-600">
                    {fmtDateTime(invoice.issued_at)}
                  </td>
                  <td className="py-2 whitespace-nowrap">
                    {!readOnly && (
                      <button
                        onClick={() => toast.error("Remove not yet implemented")}
                        className={btn("warning", "sm")}
                      >
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              ) : (
                <tr>
                  <td colSpan={17} className="py-3 text-xs text-gray-400">
                    No results found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Linked payments */}
      <Section
        title="Linked Payments"
        actionBtn={
          <ActionBtn
            onClick={() => toast.success("New linked payment not yet implemented")}
            label="New linked payment"
            color="bg-brand-600 hover:bg-brand-700"
          />
        }
      >
        <SectionTable
          headers={[
            "Code", "Linked invoices", "Invoice coverage",
            "Payment method type", "Paid at", "Amount",
            "Linked amount", "Unlinked amount",
            "Amount covered in this invoice", "Note",
          ]}
        >
          <tr>
            <td colSpan={10} className="py-3 text-xs text-gray-400">
              No results found.
            </td>
          </tr>
        </SectionTable>
      </Section>

      {/* Related files */}
      <Section
        title="Related Files"
        actionBtn={
          <ActionBtn
            onClick={() => toast.error("Upload not yet implemented")}
            label="Upload"
            color="bg-brand-600 hover:bg-brand-700"
          />
        }
      >
        <SectionTable
          headers={["File #", "Filename", "Size", "Uploaded at", "Uploaded by"]}
        >
          <tr>
            <td colSpan={5} className="py-3 text-xs text-gray-400">
              No results found.
            </td>
          </tr>
        </SectionTable>
      </Section>

      {/* Messages */}
      {!readOnly && (
        <Section title="Messages">
          <div className="px-5 py-4 space-y-3">
            <textarea
              rows={4}
              placeholder="Write a message..."
              className="w-full border border-gray-300 rounded-md p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            <button className={btn("primary", "sm")}>
              Send message to vendor
            </button>
          </div>
        </Section>
      )}
    </div>
  );
};

export default AdminVendorInvoiceDetailPage;
